import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { MdKeyboardArrowUp, MdOutlineFactory, MdOutlineHome, MdPersonOutline, MdOutlineRequestQuote } from "react-icons/md";
import { BsBoxes } from "react-icons/bs";
import { FaSlack } from "react-icons/fa";
import {
  FaCalendar,
  FaCalendarCheck,
  FaChartLine,
  FaChartBar,
  FaClipboardList,
  FaDatabase,
  FaPushed,
  FaDesktop,
  FaFileInvoiceDollar,
  FaFileExport,
  FaBrain,
  FaGraduationCap,
  FaHeadset,
  FaHome,
  FaMoneyCheck,
  FaPiggyBank,
  FaProjectDiagram,
  FaRocket,
  FaTasks,
  FaUsers,
  FaToolbox,
  FaRobot,
  FaBell,
  FaTrash,
} from "react-icons/fa";

import { AiOutlineClose, AiOutlineMenu } from "react-icons/ai";
import { BiBuildings } from "react-icons/bi";
import { FaBookReader, FaBook, FaFileContract, FaDiceD20 } from "react-icons/fa";
import { isInSelesquad, canAccessBudgetSelego, canAccessAdmin, canAccessSales } from "@/utils/roles";

import AppSelector from "../components/SelectApp";
import useStore from "../services/store";

export default function SideBar() {
  const { sideBarOpen, setSideBarOpen } = useStore();
  const { user } = useSelector((state) => state.Auth);

  useEffect(() => {
    if (window.innerWidth < 768) setSideBarOpen(false);
  }, []);

  if (!user) return null;

  if (!sideBarOpen) {
    return (
      <nav className="w-12 h-screen flex flex-col border-r border-slate-300 items-center justify-start fixed top-0 left-0 z-30">
        <div id="menu-icon" onClick={() => setSideBarOpen(true)} className="cursor-pointer mt-4">
          <AiOutlineMenu className="text-black text-2xl" />
        </div>
      </nav>
    );
  }

  return (
    <nav className="z-30 md:w-64 absolute top-0 left-0 ease-in-out duration-300">
      <div className="bg-white border-r border-slate-300 flex flex-col w-full h-screen overflow-x-hidden">
        <div className="flex justify-between items-center">
          <AppSelector userId={user._id} sideBarOpen={sideBarOpen} setSideBarOpen={setSideBarOpen} />
        </div>
        <div id="menu-icon" onClick={() => setSideBarOpen(false)} className="cursor-pointer absolute top-2 right-2">
          <AiOutlineClose className="text-black text-lg transition-opacity duration-300 opacity-100" />
        </div>
        <div className="space-y-4 flex-1 px-4 pb-8 overflow-y-scroll">
          <AccountingSidebar />
        </div>
      </div>
    </nav>
  );
}

const AccountingSidebar = () => {
  const user = useSelector((state) => state.Auth.user);
  if (user.role === "super_admin") {
    return (
      <>
        {(canAccessBudgetSelego(user) || canAccessSales(user)) && (
          <ul className="space-y-2 py-2">
            {canAccessBudgetSelego(user) && <Item title="Export" to="/export" Icon={FaFileExport} />}
            {canAccessSales(user) && <Item title="Sales" to="/sales" Icon={FaChartLine} />}
          </ul>
        )}

        {canAccessAdmin(user) && (
          <ul className="space-y-2 py-2">
            <Item title="Admin" to="/admin" Icon={FaDiceD20} />
          </ul>
        )}

        <Group title="General" Icon={MdOutlineHome}>
          <Item title="Home" to="/" Icon={FaHome} />
          <Item title="Activities" to="/activity" Icon={FaCalendarCheck} />
          <Item title="Projects" to="/project" Icon={FaProjectDiagram} />
          <Item title="People" to="/people" Icon={FaUsers} />
        </Group>

        {isInSelesquad(user) && (
          <Group title="Pilotage" Icon={FaChartLine}>
            <Item title="Dashboard" to="/piloting" Icon={FaChartLine} />
            <Item title="TACE" to="/tace" Icon={FaClipboardList} />
            <Item title="Occupation" to="/brain" Icon={FaBrain} />
            <Item title="Performance" to="/performance" Icon={FaChartBar} />
          </Group>
        )}

        <Group title="Operations" Icon={BsBoxes}>
          <Item title="Alert" to="/alert" Icon={FaBell} />
        </Group>

        <Group title="Assets" Icon={FaRocket}>
          <Item title="IT Assets" to="/device" Icon={FaDesktop} />
          <Item title="Digital Assets" to="/assets" Icon={FaRocket} />
          <Item title="Resources" to="/resources" Icon={FaBookReader} />
          <Item title="Documentation" to="/documentation" Icon={FaBook} />
          <Item title="Contracts" to="/legal_document" Icon={FaFileContract} />
        </Group>

        <Group title="Finance" Icon={FaFileInvoiceDollar}>
          <Item title="PNL Contracts" to="/pnl-contracts" Icon={FaFileContract} />
          <Item title="Invoicing" to="/invoice" Icon={FaFileInvoiceDollar} />
          <Item title="Payroll & Expenses" to="/payment" Icon={FaMoneyCheck} />
          <Item title="Bank" to="/bank" Icon={FaPiggyBank} />
          <Item title="Debt Recovery" to="/cashout" Icon={FaClipboardList} />
          <Item title="Report" to="/report" Icon={FaClipboardList} />
        </Group>

        <Group title="Administration" Icon={FaToolbox}>
          <Item title="Organisations" to="/organisation" Icon={BiBuildings} />
          <Item title="Business Units" to="/business_unit" Icon={MdOutlineFactory} />
        </Group>

      </>
    );
  }

  if (user.role === "admin") {
    return (
      <>
        {(canAccessBudgetSelego(user) || canAccessSales(user)) && (
          <ul className="space-y-2 py-2">
            {canAccessBudgetSelego(user) && <Item title="Export" to="/export" Icon={FaFileExport} />}
            {canAccessSales(user) && <Item title="Sales" to="/sales" Icon={FaChartLine} />}
          </ul>
        )}

        {canAccessAdmin(user) && (
          <ul className="space-y-2 py-2">
            <Item title="Admin" to="/admin" Icon={FaDiceD20} />
          </ul>
        )}

        <Group title="General" Icon={MdOutlineHome}>
          <Item title="Home" to="/" Icon={FaHome} />
          <Item title="Activities" to="/activity" Icon={FaCalendarCheck} />
          <Item title="Projects" to="/project" Icon={FaProjectDiagram} />
          <Item title="People" to="/people" Icon={FaUsers} />
        </Group>

        {isInSelesquad(user) && (
          <Group title="Pilotage" Icon={FaChartLine}>
            <Item title="Dashboard" to="/piloting" Icon={FaChartLine} />
            <Item title="TACE" to="/tace" Icon={FaClipboardList} />
            <Item title="Occupation" to="/brain" Icon={FaBrain} />
            <Item title="Performance" to="/performance" Icon={FaChartBar} />
          </Group>
        )}

        <Group title="Operations" Icon={BsBoxes}>
          <Item title="Alert" to="/alert" Icon={FaBell} />
        </Group>

        <Group title="Assets" Icon={FaRocket}>
          <Item title="IT Assets" to="/device" Icon={FaDesktop} />
          <Item title="Digital Assets" to="/assets" Icon={FaRocket} />
          <Item title="Resources" to="/resources" Icon={FaBookReader} />
          <Item title="Documentation" to="/documentation" Icon={FaBook} />
          <Item title="Contracts" to="/legal_document" Icon={FaFileContract} />
        </Group>

        <Group title="Finance" Icon={FaFileInvoiceDollar}>
          <Item title="PNL Contracts" to="/pnl-contracts" Icon={FaFileContract} />
          <Item title="Invoicing" to="/invoice" Icon={FaFileInvoiceDollar} />
          <Item title="Payroll & Expenses" to="/payment" Icon={FaMoneyCheck} />
          <Item title="Bank" to="/bank" Icon={FaPiggyBank} />
          <Item title="Report" to="/report" Icon={FaClipboardList} />
        </Group>

        <Group title="Administration" Icon={FaToolbox}>
          <Item title="Organisations" to="/organisation" Icon={BiBuildings} />
          <Item title="Business Units" to="/business_unit" Icon={MdOutlineFactory} />
        </Group>

      </>
    );
  }

  if (user.role === "client") {
    return (
      <>
        <ul className="space-y-2 py-2">
          <Item title="Projects" to="/project" Icon={FaProjectDiagram} />
        </ul>
      </>
    );
  }

  if (user.role === "applicant") {
    return (
      <>
        <Group title="Learning" Icon={FaGraduationCap}>
          <Item title="Workshops" to="/learn_public/" Icon={FaGraduationCap} />
        </Group>
      </>
    );
  }

  return (
    <>
      {canAccessSales(user) && (
        <ul className="space-y-2 py-2">
          <Item title="Sales" to="/sales" Icon={FaChartLine} />
        </ul>
      )}

      <Group title="Dashboard" Icon={MdOutlineHome} roles={["user"]}>
        <Item title="Home" to="/" Icon={FaHome} roles={["user"]} />
        <Item title="Activities" to="/activity" Icon={FaCalendarCheck} roles={["user"]} />
        <Item title="Projects" to="/project" Icon={FaProjectDiagram} roles={["user"]} />
      </Group>

      <Group title="Other" Icon={BsBoxes} roles={["user"]}>
        <Item title="Payment" to="/payment" Icon={FaMoneyCheck} roles={["user"]} />
        <Item title="Report" to="/report" Icon={FaClipboardList} roles={["user"]} />
        <Item title="Assets" to="/assets" Icon={FaRocket} roles={["user"]} />
      </Group>
    </>
  );
};

const Group = ({ title, children, Icon }) => {
  const { sideBarOpen, setSideBarOpen } = useStore();
  const [open, setOpen] = useState(true);

  const handleClick = () => {
    if (!sideBarOpen) setSideBarOpen(true);
    setOpen((e) => !e);
  };

  return (
    <div className="w-full">
      <button onClick={handleClick} className="transparent-btn p-2 flex items-center justify-between w-full">
        <div className="flex items-center gap-2 ">
          <Icon className="text-sky-700 text-base" />
          <h2 className={`text-sky-700 font-semibold text-base ${sideBarOpen ? "opacity-100" : "opacity-0 w-0"} transition-all`}>{title}</h2>
        </div>
        {sideBarOpen ? <MdKeyboardArrowUp className={`${open ? "rotate-0" : "-rotate-180"} transition-all text-sky-700 text-base`} /> : null}
      </button>
      <ul
        className={`sidebar ${sideBarOpen ? (open ? "open" : "") : "h-0 hidden"}`}
        style={{ maxHeight: open ? (children.length || 1) * 28 : 0, overflow: open ? "visible" : "hidden" }}>
        {children}
      </ul>
    </div>
  );
};

const Item = ({ title, to, span, Icon }) => {
  return (
    <li className="w-full">
      <NavLink to={to} className={({ isActive }) => `${isActive ? "rounded bg-gray-200" : "transparent-btn"} h-7 p-1 pl-8 flex items-center justify-between w-full" font-semibold`}>
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="text-sm" /> : null}
          <span className="text-sm truncate font-normal text-black">{title}</span>
        </div>
        {span ? span : null}
      </NavLink>
    </li>
  );
};
